import { API_URL } from '@/config/app';
import { baseApi } from '@/store/api/baseApi';
import type { RootState } from '@/store';
import type {
  BulkCustomerIdsPayload,
  BulkCustomerStatusPayload,
  CreateCustomerPayload,
  Customer,
  CustomerDetail,
  CustomerListParams,
  PaginatedCustomers,
  UpdateCustomerPayload,
} from '@/types/customer';
import { createAccessToken } from '@/utils/agent';
import { getRtkErrorMessage } from '@/utils/apiResponse';
import { isApiResponse } from '@/types/api';

function normalizeCustomerRecord(raw: Record<string, unknown>): Customer {
  if (typeof raw.firstName === 'string') {
    return raw as unknown as Customer;
  }

  const name = String(raw.name ?? '').trim();
  const nameParts = name.split(/\s+/).filter(Boolean);
  const firstName = nameParts[0] ?? name ?? 'Customer';
  const lastName = nameParts.slice(1).join(' ');

  return {
    id: String(raw.id ?? ''),
    firstName,
    lastName,
    name: name || `${firstName} ${lastName}`.trim(),
    email: String(raw.email ?? ''),
    profileImage: typeof raw.profileImage === 'string' ? raw.profileImage : undefined,
    avatar: typeof raw.avatar === 'string' ? raw.avatar : undefined,
    address: (raw.address as Customer['address']) ?? null,
    isActive: raw.isActive !== false,
    lastLoginAt: typeof raw.lastLoginAt === 'string' ? raw.lastLoginAt : undefined,
    createdAt: String(raw.createdAt ?? ''),
    updatedAt: String(raw.updatedAt ?? ''),
  };
}

function normalizeCustomerDetail(raw: Record<string, unknown>): CustomerDetail {
  const customer = normalizeCustomerRecord(raw);
  const stats = raw.stats as CustomerDetail['stats'] | undefined;
  return {
    ...customer,
    stats: stats ?? { totalOrders: 0, totalSpent: 0, totalReviews: 0 },
  };
}

function buildCustomerListQuery(params: CustomerListParams): string {
  const searchParams = new URLSearchParams();
  searchParams.set('page', String(params.page ?? 1));
  searchParams.set('limit', String(params.limit ?? 10));
  searchParams.set('sortBy', params.sortBy ?? 'createdAt');
  searchParams.set('sortOrder', params.sortOrder ?? 'desc');

  if (params.search?.trim()) searchParams.set('search', params.search.trim());
  if (params.email?.trim()) searchParams.set('email', params.email.trim());
  if (params.city?.trim()) searchParams.set('city', params.city.trim());
  if (params.state?.trim()) searchParams.set('state', params.state.trim());
  if (params.pincode?.trim()) searchParams.set('pincode', params.pincode.trim());
  if (params.status) searchParams.set('status', params.status);
  if (params.joinedFrom) searchParams.set('joinedFrom', params.joinedFrom);
  if (params.joinedTo) searchParams.set('joinedTo', params.joinedTo);

  return searchParams.toString();
}

export const customersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCustomers: builder.query<PaginatedCustomers, CustomerListParams>({
      query: (params) => ({
        url: `/customers?${buildCustomerListQuery(params)}`,
      }),
      transformResponse: (response: PaginatedCustomers) => ({
        ...response,
        results: (response.results ?? []).map((item) =>
          normalizeCustomerRecord(item as unknown as Record<string, unknown>),
        ),
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${buildCustomerListQuery(queryArgs)})`,
      forceRefetch: ({ currentArg, previousArg }) =>
        buildCustomerListQuery(currentArg ?? {}) !== buildCustomerListQuery(previousArg ?? {}),
      providesTags: (result) =>
        result?.results
          ? [
              ...result.results.map(({ id }) => ({ type: 'Customers' as const, id })),
              { type: 'Customers', id: 'LIST' },
            ]
          : [{ type: 'Customers', id: 'LIST' }],
    }),
    getCustomer: builder.query<CustomerDetail, string>({
      query: (id) => `/customers/${id}`,
      transformResponse: (response: CustomerDetail) =>
        normalizeCustomerDetail(response as unknown as Record<string, unknown>),
      providesTags: (_result, _error, id) => [{ type: 'Customers', id }],
    }),
    createCustomer: builder.mutation<Customer, CreateCustomerPayload>({
      query: (body) => ({ url: '/customers/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Customers', id: 'LIST' }],
    }),
    updateCustomer: builder.mutation<Customer, { id: string; body: UpdateCustomerPayload }>({
      query: ({ id, body }) => ({ url: `/customers/update/${id}`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Customers', id },
        { type: 'Customers', id: 'LIST' },
      ],
    }),
    uploadCustomerImage: builder.mutation<Customer, { id: string; formData: FormData }>({
      async queryFn({ id, formData }, api) {
        const { accessToken } = (api.getState() as RootState).auth;

        try {
          const response = await fetch(`${API_URL}/customers/upload-image/${id}`, {
            method: 'POST',
            headers: {
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
              'x-access-token': createAccessToken(),
            },
            body: formData,
          });

          const json: unknown = await response.json();

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: getRtkErrorMessage({ data: json }) ?? 'Image upload failed.',
              },
            };
          }

          const customer = isApiResponse<Customer>(json) ? json.data : (json as Customer);
          const message = isApiResponse<Customer>(json) ? json.message : undefined;

          return {
            data: customer,
            ...(message ? { meta: { message } } : {}),
          };
        } catch {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: 'Image upload failed.',
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Customers', id },
        { type: 'Customers', id: 'LIST' },
      ],
    }),
    removeCustomerImage: builder.mutation<Customer, string>({
      query: (id) => ({ url: `/customers/remove-image/${id}`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Customers', id },
        { type: 'Customers', id: 'LIST' },
      ],
    }),
    deleteCustomer: builder.mutation<void, string>({
      query: (id) => ({ url: `/customers/delete/${id}`, method: 'POST' }),
      invalidatesTags: [{ type: 'Customers', id: 'LIST' }],
    }),
    bulkUpdateCustomerStatus: builder.mutation<{ count: number }, BulkCustomerStatusPayload>({
      query: (body) => ({ url: '/customers/bulk-status', method: 'POST', body }),
      invalidatesTags: [{ type: 'Customers', id: 'LIST' }],
    }),
    bulkDeleteCustomers: builder.mutation<{ count: number }, BulkCustomerIdsPayload>({
      query: (body) => ({ url: '/customers/bulk-delete', method: 'POST', body }),
      invalidatesTags: [{ type: 'Customers', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetCustomersQuery,
  useLazyGetCustomersQuery,
  useGetCustomerQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useUploadCustomerImageMutation,
  useRemoveCustomerImageMutation,
  useDeleteCustomerMutation,
  useBulkUpdateCustomerStatusMutation,
  useBulkDeleteCustomersMutation,
} = customersApi;
